import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { FileUp, FileText, Trash2, Download, FolderOpen } from "lucide-react";
import axios from "axios";

const DOC_TYPES = [
  { value: "fattura", label: "Fattura" },
  { value: "ricevuta", label: "Ricevuta" },
  { value: "contratto", label: "Contratto" },
  { value: "doc_ota", label: "Documento OTA" },
  { value: "fiscale", label: "Documento Fiscale" },
  { value: "altro", label: "Altro" },
];

const MONTH_LABELS = { "01": "Gennaio", "02": "Febbraio", "03": "Marzo", "04": "Aprile", "05": "Maggio", "06": "Giugno", "07": "Luglio", "08": "Agosto", "09": "Settembre", "10": "Ottobre", "11": "Novembre", "12": "Dicembre" };
const MONTH_OPTIONS = Object.entries(MONTH_LABELS).map(([v, l]) => ({ value: v, label: l }));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentsPage() {
  const { user, API } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterProp, setFilterProp] = useState("all");
  const [filterYear, setFilterYear] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ property_id: "", month: "", doc_type: "fattura", description: "" });
  const [file, setFile] = useState(null);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    Promise.all([axios.get(`${API}/documents`), axios.get(`${API}/properties`)])
      .then(([d, p]) => { setDocuments(d.data); setProperties(p.data); })
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  }, [API]);

  const filtered = useMemo(() => {
    let result = documents;
    if (filterProp !== "all") result = result.filter(d => d.property_id === filterProp);
    if (filterYear !== "all") result = result.filter(d => d.month?.startsWith(filterYear));
    if (filterMonth !== "all") result = result.filter(d => d.month?.endsWith(`-${filterMonth}`) || d.month?.split("-")[1] === filterMonth);
    if (filterType !== "all") result = result.filter(d => d.doc_type === filterType);
    return result;
  }, [documents, filterProp, filterYear, filterMonth, filterType]);

  const years = useMemo(() => [...new Set(documents.map(d => d.month?.split("-")[0]).filter(Boolean))].sort().reverse(), [documents]);

  const handleUpload = async () => {
    if (!file || !form.property_id || !form.month) {
      toast.error("Seleziona file, proprietà e mese");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("property_id", form.property_id);
    formData.append("month", form.month);
    formData.append("doc_type", form.doc_type);
    formData.append("description", form.description);
    try {
      const res = await axios.post(`${API}/documents`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      const prop = properties.find(p => p.id === res.data.property_id);
      res.data.property_name = prop?.name || "N/A";
      setDocuments(prev => [res.data, ...prev]);
      toast.success("Documento caricato");
      setDialogOpen(false);
      setFile(null);
      setForm({ property_id: "", month: "", doc_type: "fattura", description: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    try {
      await axios.delete(`${API}/documents/${id}`);
      setDocuments(prev => prev.filter(d => d.id !== id));
      toast.success("Documento eliminato");
    } catch { toast.error("Errore"); }
  };

  const currentMonth = new Date().toISOString().slice(0, 7);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-300 border-t-stone-900" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="documents-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">Documenti</h1>
          <p className="text-stone-500 mt-1 text-sm">Gestisci fatture, ricevute e documenti per proprietà</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)} data-testid="upload-document-button" className="bg-stone-900 hover:bg-stone-800 text-white">
            <FileUp className="h-4 w-4 mr-2" /> Carica Documento
          </Button>
        )}
      </div>

      <div className="flex gap-3 mb-6 flex-wrap">
        <Select value={filterProp} onValueChange={setFilterProp}>
          <SelectTrigger data-testid="doc-property-filter" className="w-52 bg-white"><SelectValue placeholder="Proprietà" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le proprietà</SelectItem>
            {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterYear} onValueChange={v => { setFilterYear(v); }}>
          <SelectTrigger data-testid="doc-year-filter" className="w-32 bg-white"><SelectValue placeholder="Anno" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli anni</SelectItem>
            {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger data-testid="doc-month-filter" className="w-40 bg-white"><SelectValue placeholder="Mese" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i mesi</SelectItem>
            {MONTH_OPTIONS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger data-testid="doc-type-filter" className="w-44 bg-white"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <Card className="border border-stone-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <FolderOpen className="h-12 w-12 text-stone-300 mx-auto mb-4" strokeWidth={1} />
            <p className="text-stone-500">Nessun documento trovato</p>
            {isAdmin && <Button onClick={() => setDialogOpen(true)} className="mt-4 bg-stone-900 text-white">Carica il primo documento</Button>}
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-stone-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="text-xs font-medium text-stone-500">File</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Proprietà</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Mese</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Tipo</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Descrizione</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Data</TableHead>
                <TableHead className="text-xs w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(d => (
                <TableRow key={d.id} data-testid={`document-row-${d.id}`} className="hover:bg-stone-50">
                  <TableCell className="text-sm font-medium text-stone-900 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-stone-400 shrink-0" />
                    <span className="truncate max-w-[200px]">{d.file_name}</span>
                  </TableCell>
                  <TableCell className="text-sm text-stone-600">{d.property_name}</TableCell>
                  <TableCell className="text-sm font-mono text-stone-600">{d.month}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] capitalize">{DOC_TYPES.find(t => t.value === d.doc_type)?.label || d.doc_type}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-stone-500 truncate max-w-[200px]">{d.description || "-"}</TableCell>
                  <TableCell className="text-xs font-mono text-stone-400">{d.created_at ? new Date(d.created_at).toLocaleDateString("it-IT") : ""}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <a href={`${BACKEND_URL}${d.file_url}`} target="_blank" rel="noopener noreferrer">
                        <Button size="icon" variant="ghost" className="h-7 w-7"><Download className="h-3.5 w-3.5 text-stone-500" /></Button>
                      </a>
                      {isAdmin && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(d.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="upload-document-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Carica Documento</DialogTitle>
            <DialogDescription className="text-sm text-stone-500">Carica fatture, ricevute o altri documenti</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-stone-600">File</Label>
              <Input data-testid="doc-file-input" type="file" onChange={e => setFile(e.target.files[0])} className="mt-1 bg-white" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
            </div>
            <div>
              <Label className="text-xs text-stone-600">Proprietà</Label>
              <Select value={form.property_id} onValueChange={v => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="doc-property-select" className="mt-1 bg-white"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-stone-600">Mese</Label>
                <Input data-testid="doc-month-input" type="month" value={form.month || currentMonth} onChange={e => setForm({ ...form, month: e.target.value })} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Tipo</Label>
                <Select value={form.doc_type} onValueChange={v => setForm({ ...form, doc_type: v })}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-stone-600">Descrizione (opzionale)</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Es: Fattura pulizie gennaio" className="mt-1 bg-white" />
            </div>
            <Button onClick={handleUpload} disabled={uploading} data-testid="doc-upload-submit" className="w-full bg-stone-900 hover:bg-stone-800 text-white">
              {uploading ? "Caricamento..." : "Carica Documento"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
